import {
  Card,
  CardBody,
  CardHeader,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Grid,
  Input,
  Select,
} from '@chakra-ui/react';
import { ErrorMessage } from '@hookform/error-message';
import { FeatureVisibility } from '@prisma/client';
import { SnapSiteConfigEditFormDataUnvalidated } from '@snap-types/models/snap-site-config';
import React from 'react';
import { get, UseFormReturn } from 'react-hook-form';

type PartInsightPanelProps = {
  form: UseFormReturn<SnapSiteConfigEditFormDataUnvalidated>;
};

export const PartInsightsPanel = React.forwardRef<
  HTMLFormElement,
  PartInsightPanelProps
>(({ form }) => {
  return (
    <Flex px={4} direction="column" gap={4}>
      <FormControl isInvalid={!!form.formState.errors.partSpecTable}>
        <FormLabel htmlFor="part_spec_table_input">
          Part Insights Visibility
        </FormLabel>

        <Select
          {...form.register('partInsightsVisible')}
          id="part_insights_visible_input"
        >
          <option value={FeatureVisibility.NONE}>No One</option>
          <option value={FeatureVisibility.SUPER_ADMIN}>
            Super Admins Only
          </option>
          <option value={FeatureVisibility.ALL}>All Users</option>
        </Select>

        <ErrorMessage
          name="partInsightsVisible"
          render={({ message }) => (
            <FormErrorMessage>{message}</FormErrorMessage>
          )}
        />
      </FormControl>

      <Card
        variant="outline"
        role="group"
        data-group-scope="part-insights"
        borderLeftColor="green"
        borderLeftWidth={6}
      >
        <CardHeader pt={2} pb={2} fontWeight="bold">
          Price Insights
        </CardHeader>
        <CardBody pt={2} gap={4} display="flex" flexDirection="column">
          <Grid
            gridTemplateColumns={{
              base: '1fr',
              md: '1fr 1fr',
              lg: '1fr 1fr 1fr',
            }}
            gap={4}
            width="auto"
          >
            <FormControl
              isInvalid={
                !!get(form.formState.errors, 'partInsightsMaxDaysForRecent')
              }
            >
              <FormLabel htmlFor="part_insights_max_days_for_recent_input">
                Max age to be <strong>Recent</strong>? (in days)
              </FormLabel>

              <Input
                id="part_insights_max_days_for_recent_input"
                type="number"
                inputMode="numeric"
                {...form.register('partInsightsMaxDaysForRecent')}
                min={0}
              />

              <ErrorMessage
                name="partInsightsMaxDaysForRecent"
                render={({ message }) => (
                  <FormErrorMessage>{message}</FormErrorMessage>
                )}
              />
            </FormControl>

            <FormControl
              isInvalid={
                !!get(form.formState.errors, 'partInsightsMaxDaysForInclusion')
              }
            >
              <FormLabel htmlFor="part_insights_max_days_for_inclusion_input">
                Max age to be included <strong>at all</strong>? (in days)
              </FormLabel>

              <Input
                id="part_insights_max_days_for_inclusion_input"
                type="number"
                inputMode="numeric"
                {...form.register('partInsightsMaxDaysForInclusion')}
                min={0}
              />

              <ErrorMessage
                name="partInsightsMaxDaysForInclusion"
                render={({ message }) => (
                  <FormErrorMessage>{message}</FormErrorMessage>
                )}
              />
            </FormControl>

            <FormControl
              isInvalid={
                !!get(form.formState.errors, 'partInsightsHighWinRate')
              }
            >
              <FormLabel htmlFor="part_insights_high_win_rate_input">
                High Win Rate Threshold
              </FormLabel>

              <Input
                id="part_insights_high_win_rate_input"
                type="number"
                inputMode="numeric"
                {...form.register('partInsightsHighWinRate')}
                min={0}
                max={1}
              />

              <ErrorMessage
                name="partInsightsHighWinRate"
                render={({ message }) => (
                  <FormErrorMessage>{message}</FormErrorMessage>
                )}
              />
              <FormHelperText>
                As a decimal percent between <strong>0.00</strong> and{' '}
                <strong>1.00</strong>
              </FormHelperText>
            </FormControl>
          </Grid>
        </CardBody>
      </Card>
      <Card
        variant="outline"
        role="group"
        data-group-scope="part-insights"
        borderLeftColor="orange.300"
        borderLeftWidth={6}
      >
        <CardHeader pt={2} pb={2} fontWeight="bold">
          Lead Time Insights
        </CardHeader>
        <CardBody pt={2} gap={4} display="flex" flexDirection="column">
          <Grid
            gridTemplateColumns={{
              base: '1fr',
              md: '1fr 1fr',
              lg: '1fr 1fr 1fr',
            }}
            gap={4}
            width="auto"
          >
            <FormControl
              isInvalid={
                !!get(form.formState.errors, 'partInsightsLeadTimeRangeLow')
              }
            >
              <FormLabel htmlFor="part_insights_lead_time_range_low">
                'Slightly' is More Than . . .
              </FormLabel>

              <Input
                id="part_insights_lead_time_range_low"
                type="number"
                inputMode="numeric"
                {...form.register('partInsightsLeadTimeRangeLow')}
                min={0}
              />

              <ErrorMessage
                name="partInsightsLeadTimeRangeLow"
                render={({ message }) => (
                  <FormErrorMessage>{message}</FormErrorMessage>
                )}
              />

              <FormHelperText>
                As a decimal percent between <strong>0.00</strong> and{' '}
                <strong>1.00</strong>
              </FormHelperText>
            </FormControl>

            <FormControl
              isInvalid={
                !!get(form.formState.errors, 'partInsightsLeadTimeRangeHigh')
              }
            >
              <FormLabel htmlFor="part_insights_lead_time_range_high">
                'Significantly' is More Than . . .
              </FormLabel>

              <Input
                id="part_insights_lead_time_range_low"
                type="number"
                inputMode="numeric"
                {...form.register('partInsightsLeadTimeRangeHigh')}
                min={0}
              />

              <ErrorMessage
                name="partInsightsLeadTimeRangeHigh"
                render={({ message }) => (
                  <FormErrorMessage>{message}</FormErrorMessage>
                )}
              />

              <FormHelperText>
                As a decimal percent between <strong>0.00</strong> and{' '}
                <strong>1.00</strong>
              </FormHelperText>
            </FormControl>

            <FormControl
              isInvalid={
                !!get(
                  form.formState.errors,
                  'partInsightsLeadTimeMaxDaysForInclusion',
                )
              }
            >
              <FormLabel htmlFor="part_insights_lead_time_max_days_for_inclusion">
                Max Order Age to include in Lead Time Average (in days)
              </FormLabel>

              <Input
                id="part_insights_lead_time_max_days_for_inclusion"
                type="number"
                inputMode="numeric"
                {...form.register('partInsightsLeadTimeMaxDaysForInclusion')}
                min={0}
              />

              <ErrorMessage
                name="partInsightsLeadTimeMaxDaysForInclusion"
                render={({ message }) => (
                  <FormErrorMessage>{message}</FormErrorMessage>
                )}
              />
            </FormControl>
          </Grid>
        </CardBody>
      </Card>
    </Flex>
    //   </Show>
    // </FormPanel>
  );
});
